import { FC, useCallback } from 'react'
import PurchasesBlock from './PurchasesBlock'
import classes from './index.module.css'
import LinksPurchasesBlock from './LinksPurchasesBlock'
import { useAsyncRetry } from 'react-use'
import { crmApi } from '@/api'
import type { LeadProfileLead } from '../../LeadProfileModal.types'
import { useLeadRefetch } from '../../LeadRefetchContext'

interface WideShoppingContentProps {
  lead: LeadProfileLead
  currentLeadFunnelId?: number
  onKanbanCardSync?: () => void
}

const WideShoppingContent: FC<WideShoppingContentProps> = ({
  lead,
  currentLeadFunnelId,
  onKanbanCardSync,
}) => {
  const {
    handleRefetchLeadInModal,
    handleRefetchLeadsOnClose,
    initialFunnelIdForRefetch,
    isRefetchingLeadCard,
    selectedFunnelId,
  } = useLeadRefetch()

  const { value: excludedIds, retry: retryExcludedFromPayments } = useAsyncRetry(
    async () => {
      if (!lead.user_id) {
        return { classIds: [] as number[], directionIds: [] as number[] }
      }
      const res = await crmApi.getLeadPayments({ user_id: lead.user_id })
      const data = res?.data?.data
      const classIds =
        data?.classes
          ?.map((c: { class?: { id?: number | null } }) => c?.class?.id)
          ?.filter((id): id is number => typeof id === 'number') ?? []
      const directionIds =
        data?.directions
          ?.map((c: { direction?: { id?: number | null } }) => c?.direction?.id)
          ?.filter((id): id is number => typeof id === 'number') ?? []
      return { classIds, directionIds }
    },
    [lead.user_id],
  )

  const excludedClassIds = excludedIds?.classIds ?? []
  const excludedDirectionIds = excludedIds?.directionIds ?? []

  const refetchExcludedIds = useCallback(() => {
    void retryExcludedFromPayments()
  }, [retryExcludedFromPayments])

  const syncLeadBindingFromShopping = useCallback(async () => {
    const funnelId =
      selectedFunnelId != null && Number(selectedFunnelId) > 0
        ? selectedFunnelId
        : initialFunnelIdForRefetch
    await handleRefetchLeadInModal(funnelId)
    handleRefetchLeadsOnClose()
    void retryExcludedFromPayments()
  }, [
    handleRefetchLeadInModal,
    handleRefetchLeadsOnClose,
    initialFunnelIdForRefetch,
    retryExcludedFromPayments,
    selectedFunnelId,
  ])

  return (
    <div className={classes.wrapper}>
      <PurchasesBlock
        lead={lead}
        currentLeadFunnelId={currentLeadFunnelId}
        excludedClassIds={excludedClassIds}
        excludedDirectionIds={excludedDirectionIds}
        refetchExcludedIds={refetchExcludedIds}
        onKanbanCardSync={onKanbanCardSync}
      />
      <LinksPurchasesBlock
        lead={lead}
        currentLeadFunnelId={currentLeadFunnelId}
        excludedClassIds={excludedClassIds}
        excludedDirectionIds={excludedDirectionIds}
        isSyncingLeadBinding={isRefetchingLeadCard}
        onSyncLeadBinding={syncLeadBindingFromShopping}
      />
    </div>
  )
}

export default WideShoppingContent
